import type { components } from './api.generated';
import type { EmoteName, Gesture } from './emotes';
import { randomId } from './randomId';

type Result = components['schemas']['EmoteResult'];

export class EmoteSender {
  private pending: {
    id: string;
    resolve: (gesture: Gesture) => void;
    reject: (error: Error) => void;
    timeout: ReturnType<typeof setTimeout>;
  } | null = null;

  constructor(private readonly socket: () => WebSocket | null) {}

  send(name: EmoteName): Promise<Gesture> {
    const socket = this.socket();
    if (!socket || socket.readyState !== WebSocket.OPEN)
      return Promise.reject(new Error('The shared city is not connected.'));
    if (this.pending)
      return Promise.reject(new Error('Wait for the city to confirm your gesture.'));
    const id = randomId();
    return new Promise((resolve, reject) => {
      this.pending = {
        id,
        resolve,
        reject,
        timeout: setTimeout(() => {
          this.fail('The city did not confirm your gesture.');
        }, 5000),
      };
      const command: components['schemas']['EmoteRequest'] = {
        type: 'emote',
        request_id: id,
        name,
      };
      try {
        socket.send(JSON.stringify(command));
      } catch {
        this.fail('The shared city is not connected.');
      }
    });
  }

  receive(result: Result): void {
    const pending = this.pending;
    if (!pending || result.request_id !== pending.id) return;
    clearTimeout(pending.timeout);
    this.pending = null;
    if (result.status === 'started' && result.emote) pending.resolve(result.emote);
    else
      pending.reject(
        new Error(
          result.status === 'busy'
            ? 'Let the gesture finish.'
            : 'This gesture is unavailable for your citizen.'
        )
      );
  }

  fail(message = 'The shared city is not connected.'): void {
    const pending = this.pending;
    if (!pending) return;
    clearTimeout(pending.timeout);
    this.pending = null;
    pending.reject(new Error(message));
  }
}
